import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  ArrowUpRight, ArrowRight, Globe, Smartphone, BrainCircuit,
  LineChart, Server, PaintBucket, Sparkles,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import ProductsSection from "@/components/ProductsSection";
import AnimatedHeading from "@/components/AnimatedHeading";
import AnimatedCounter from "@/components/AnimatedCounter";
import Industries from "@/components/Industries";
import Process from "@/components/Process";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import SEO from "@/components/SEO";
import ParticlesBackground from "@/components/ParticlesBackground";
import { SERVICES, STATS, WHY_US } from "@/data/site";

const ICONS = { Globe, Smartphone, BrainCircuit, LineChart, Server, PaintBucket };

const MARQUEE_BRANDS = [
  "SF Store", "ShopMyTrendz", "AirMyCart", "Aspire Group", "Infinity Stones",
  "Kenaf Coir", "VSpec Global", "Align Technologies", "Gurus Industries",
  "Anand Nivash", "RNK Builders", "VDesign HK",
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  // Hero parallax & fade-out as you scroll past it
  const heroTextY = useTransform(heroProgress, [0, 1], [0, -120]);
  const heroTextOpacity = useTransform(heroProgress, [0, 0.7], [1, 0]);
  const gridY = useTransform(heroProgress, [0, 1], [0, 160]);

  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://amagictechsolutions.com/#organization",
        "name": "AmagicTech Solutions",
        "url": "https://amagictechsolutions.com",
        "logo": "https://amagictechsolutions.com/brand/favicon-512.png",
        "image": "https://amagictechsolutions.com/brand/favicon-512.png",
        "description": "AmagicTech Solutions is a multidisciplinary engineering studio specializing in high-performance web applications, mobile apps, AI copilots, and cloud architecture.",
        "sameAs": [
          "https://www.linkedin.com/company/amagictech-solutions",
          "https://github.com/amagictech"
        ],
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "telephone": "+91-98432-44699",
            "contactType": "sales",
            "areaServed": ["IN", "OM", "US", "CA", "GB", "AE"],
            "availableLanguage": ["English", "Tamil", "Hindi", "Arabic"]
          },
          {
            "@type": "ContactPoint",
            "telephone": "+968-9123-4567",
            "contactType": "customer service",
            "areaServed": "OM",
            "availableLanguage": ["English", "Arabic"]
          }
        ],
        "address": [
          {
            "@type": "PostalAddress",
            "streetAddress": "Civil Aerodrome Post, SITRA",
            "addressLocality": "Coimbatore",
            "addressRegion": "Tamil Nadu",
            "postalCode": "641014",
            "addressCountry": "IN"
          },
          {
            "@type": "PostalAddress",
            "streetAddress": "Al Khuwair, Way 3514",
            "addressLocality": "Muscat",
            "addressRegion": "Muscat Governorate",
            "addressCountry": "OM"
          }
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://amagictechsolutions.com/#website",
        "url": "https://amagictechsolutions.com",
        "name": "AmagicTech Solutions",
        "publisher": {
          "@id": "https://amagictechsolutions.com/#organization"
        }
      },
      {
        "@type": "ProfessionalService",
        "@id": "https://amagictechsolutions.com/#service",
        "name": "AmagicTech Solutions Digital Product Studio",
        "url": "https://amagictechsolutions.com",
        "priceRange": "$$",
        "image": "https://amagictechsolutions.com/brand/favicon-512.png",
        "telephone": "+91-98432-44699",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Coimbatore",
          "addressRegion": "Tamil Nadu",
          "addressCountry": "IN"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Software Engineering Services",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Custom Web Application Development" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Mobile App Development (iOS & Android)" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI Integrations & Custom Copilots" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Cloud Architecture & DevOps" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Enterprise IT Solutions & ERP" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Product UI/UX Design & Prototyping" } }
          ]
        }
      }
    ]
  };

  return (
    <main data-testid="home-page" className="pt-20">
      <SEO 
        title="AmagicTech Solutions | Web, Mobile, AI & Digital Services"
        description="AmagicTech Solutions is an engineering studio delivering high-impact web apps, mobile solutions, AI systems, and cloud infrastructure for clients in India, Oman, and worldwide."
        url="/"
        schema={homeSchema}
      />
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <motion.div
          style={{ y: gridY, willChange: "transform" }}
          className="absolute inset-0 grid-bg opacity-[0.5] pointer-events-none"
        />
        <ParticlesBackground />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#FAFAF7] pointer-events-none" />
        <motion.div
          style={{ y: heroTextY, opacity: heroTextOpacity }}
          className="container-tight relative min-h-[85vh] flex flex-col justify-center pt-16 pb-24"
        >
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-1 text-[11px] font-mono-tech tracking-[0.2em] uppercase text-zinc-600">
                <Sparkles className="w-3 h-3" /> Open for new projects · Q1 2026
              </span>
            </div>
          </Reveal>

          <AnimatedHeading
            as="h1"
            text="We design, build & scale digital products that compound."
            highlight={["compound."]}
            highlightClass="text-zinc-400"
            delay={0.1}
            className="mt-8 font-display text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tighter text-zinc-900 max-w-5xl"
          />

          <Reveal delay={0.55} className="mt-8 grid md:grid-cols-12 gap-6">
            <p className="md:col-start-6 md:col-span-7 text-zinc-600 text-lg leading-relaxed max-w-2xl">
              AmagicTech Solutions is a multidisciplinary studio of engineers, designers,
              and strategists. From composable commerce to AI copilots and modern mobile apps —
              we ship the kind of work that earns trust and grows businesses.
            </p>
          </Reveal>

          <Reveal delay={0.7} className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3.5 text-sm font-medium hover:bg-zinc-700 transition-colors"
            >
              Start your project <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/portfolio"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-900 px-6 py-3.5 text-sm hover:border-zinc-600 hover:bg-white transition-colors"
            >
              View recent work <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>

          {/* hero footer mono caption */}
          <Reveal delay={0.85} className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px border border-zinc-200 bg-zinc-100">
            {STATS.map((s, idx) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.9 + idx * 0.07, ease: [0.22, 0.61, 0.36, 1] }}
                className="bg-[#FAFAF7] p-6"
              >
                <p className="font-mono-tech text-3xl md:text-4xl text-zinc-900 tracking-tight">
                  <AnimatedCounter value={s.value} duration={1.8} />
                </p>
                <p className="label-mono mt-2">{s.label}</p>
              </motion.div>
            ))}
          </Reveal>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-zinc-200 py-6 md:py-8 overflow-hidden bg-white/50 relative">
        <div className="mask-fade-edges overflow-hidden w-full">
          <div className="flex marquee-track whitespace-nowrap items-center gap-8 sm:gap-12 md:gap-16">
            {Array.from({ length: 20 }, (_, i) => {
              const index = i + 1;
              const ext = index === 20 ? 'webp' : 'png';
              const src = `/clients/client${index}.${ext}`;
              return (
                <div key={i} className="flex items-center justify-center h-8 sm:h-10 md:h-12 w-auto shrink-0 px-2">
                  <img
                    src={src}
                    alt={`Client ${index}`}
                    loading="lazy"
                    className="max-h-full max-w-[105px] sm:max-w-[125px] md:max-w-[145px] object-contain grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300 pointer-events-none select-none"
                  />
                </div>
              );
            })}
            {Array.from({ length: 20 }, (_, i) => {
              const index = i + 1;
              const ext = index === 20 ? 'webp' : 'png';
              const src = `/clients/client${index}.${ext}`;
              return (
                <div key={`dup-${i}`} className="flex items-center justify-center h-8 sm:h-10 md:h-12 w-auto shrink-0 px-2" aria-hidden="true">
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="max-h-full max-w-[105px] sm:max-w-[125px] md:max-w-[145px] object-contain grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300 pointer-events-none select-none"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <Industries index="/01" />

      {/* SERVICES */}
      <section className="py-24 md:py-32">
        <div className="container-tight">
          <Reveal>
            <SectionLabel index="/02" testid="home-services-label">What we do</SectionLabel>
          </Reveal>
          <Reveal delay={0.05} className="mt-6 grid md:grid-cols-12 gap-6">
            <h2 className="md:col-span-7 font-display text-4xl md:text-5xl lg:text-6xl text-zinc-900 tracking-tighter leading-[1.02]">
              Six disciplines.
              <br />
              <span className="text-zinc-500">One delivery team.</span>
            </h2>
            <p className="md:col-span-5 md:pt-3 text-zinc-600 leading-relaxed">
              We work as a single pod — engineering, design, and growth together —
              so handoffs disappear and your product ships faster.
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-100 border border-zinc-200">
            {SERVICES.map((s, idx) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={s.id} delay={idx * 0.04}>
                  <Link
                    to="/services"
                    data-testid={`home-service-${s.id}`}
                    className="group bg-[#FAFAF7] p-8 lg:p-10 h-full flex flex-col justify-between hover:bg-white transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <span className="font-mono-tech text-xs text-zinc-500">{s.code}</span>
                      <Icon className="w-5 h-5 text-zinc-600 group-hover:text-zinc-900 transition-colors" />
                    </div>
                    <div className="mt-16">
                      <h3 className="font-display text-2xl text-zinc-900 tracking-tight">{s.title}</h3>
                      <p className="mt-3 text-zinc-600 text-[15px] leading-relaxed">{s.short}</p>
                      <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-zinc-900 opacity-70 group-hover:opacity-100 transition-opacity">
                        Learn more <ArrowUpRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <Process index="/03" />

      {/* PRODUCTS (SaaS we own) */}
      <ProductsSection index="/04" />

      {/* TESTIMONIALS */}
      <Testimonials index="/05" />

      {/* WHY US */}
      <section className="py-24 md:py-32 border-t border-zinc-200">
        <div className="container-tight grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <SectionLabel index="/06" testid="home-why-label">Why AmagicTech</SectionLabel>
              <h2 className="mt-6 font-display text-4xl md:text-5xl text-zinc-900 tracking-tighter leading-[1.05]">
                The studio
                <br /><span className="text-zinc-500">that ships.</span>
              </h2>
              <p className="mt-6 text-zinc-600 leading-relaxed">
                We prioritize clarity, transparent roadmaps, and measurable success criteria —
                with engineering rigor on every release.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-100 border border-zinc-200">
            {WHY_US.map((w, idx) => (
              <Reveal key={w.title} delay={idx * 0.05}>
                <div className="bg-[#FAFAF7] p-7 h-full">
                  <span className="font-mono-tech text-xs text-zinc-500">0{idx + 1}</span>
                  <h3 className="mt-6 font-display text-xl text-zinc-900 tracking-tight">{w.title}</h3>
                  <p className="mt-2 text-zinc-600 text-[15px] leading-relaxed">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ index="/07" labelText="Common questions" />

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-zinc-200">
        <div className="container-tight">
          <Reveal>
            <div className="border border-zinc-200 p-10 md:p-16 relative overflow-hidden">
              <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
              <div className="relative grid md:grid-cols-12 gap-8 items-end">
                <div className="md:col-span-8">
                  <SectionLabel testid="home-cta-label">Ready when you are</SectionLabel>
                  <h2 className="mt-6 font-display text-4xl md:text-6xl text-zinc-900 tracking-tighter leading-[1.02]">
                    Let&apos;s build the next
                    <br />chapter of your business.
                  </h2>
                </div>
                <div className="md:col-span-4 flex md:justify-end">
                  <Link
                    to="/contact"
                    data-testid="home-cta-contact"
                    className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3.5 text-sm font-medium hover:bg-zinc-700 transition-colors"
                  >
                    Start the conversation <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
